/**
 * TenantDataIntegrity — firm-scoped data integrity health page.
 *
 * Shows only issues whose `by_tenant` count includes this tenant. Firm owners
 * see what's drifting in their data and can request the platform to re-run a
 * check. Auto-fixes are applied by the platform-wide cron, so this page is
 * mostly a transparency dashboard.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, RefreshCw, AlertTriangle, Info, AlertCircle, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Ui";

const SEV = {
    critical: { color: "text-red-400",   border: "border-red-500/40",   bg: "bg-red-500/5",   Icon: AlertCircle },
    warning:  { color: "text-amber-400", border: "border-amber-500/40", bg: "bg-amber-500/5", Icon: AlertTriangle },
    info:     { color: "text-sky-400",   border: "border-sky-500/40",   bg: "bg-sky-500/5",   Icon: Info },
};

export default function TenantDataIntegrity() {
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/tenant/data-integrity");
            setIssues(data?.issues || []);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const totalAffected = issues.reduce((s, i) => s + (i.total_for_tenant || 0), 0);

    return (
        <div className="space-y-6">
            <PageHeader
                title="Data Integrity"
                subtitle="Issues the platform's nightly audit found in YOUR firm's data. Most are auto-fixed at 08:30 IST and after every eCourts sync — anything below means it needs attention or a code fix."
                icon={<ShieldCheck className="w-8 h-8" strokeWidth={1.5}/>}
            />

            <div className="grid grid-cols-2 gap-3" data-testid="tenant-integrity-summary">
                <div className="bg-muted border border-border p-4">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Total issues in your data</div>
                    <div className={`text-3xl font-light ${totalAffected === 0 ? "text-emerald-400" : "text-amber-400"}`}>
                        {totalAffected}
                    </div>
                </div>
                <div className="bg-muted border border-border p-4 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Audit refresh</div>
                        <div className="text-xs text-muted-foreground">Click to re-pull the latest platform audit results.</div>
                    </div>
                    <button onClick={load} disabled={loading}
                        data-testid="tenant-integrity-refresh"
                        className="inline-flex items-center gap-2 px-4 py-2 border border-primary text-primary text-xs uppercase tracking-[0.16em] hover:bg-primary hover:text-primary-foreground disabled:opacity-30">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5}/>
                        Refresh
                    </button>
                </div>
            </div>

            {loading && <div className="text-xs text-muted-foreground p-4">Loading…</div>}

            {!loading && issues.length === 0 && (
                <div className="border border-emerald-500/40 bg-emerald-500/5 p-6 text-center" data-testid="tenant-integrity-all-healthy">
                    <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400 mb-3" strokeWidth={1}/>
                    <div className="text-emerald-400 text-sm">All clear — no integrity issues in your firm's data.</div>
                    <div className="text-[11px] text-muted-foreground mt-2">Last audit ran at the most recent 08:30 IST cron or eCourts refresh.</div>
                </div>
            )}

            <div className="space-y-2">
                {issues.map(i => {
                    const sev = SEV[i.severity] || SEV.info;
                    const { Icon } = sev;
                    return (
                        <div key={i.key} className={`border ${sev.border} ${sev.bg} p-4`}
                            data-testid={`tenant-integrity-issue-${i.key}`}>
                            <div className="flex items-start gap-3">
                                <Icon className={`w-5 h-5 ${sev.color} mt-0.5`} strokeWidth={1.5}/>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm text-foreground">{i.label}</span>
                                        <span className={`text-[10px] px-2 py-0.5 uppercase tracking-[0.16em] ${sev.color} border ${sev.border}`}>{i.severity}</span>
                                        <span className={`text-[10px] ${sev.color}`}>{i.total_for_tenant} in your firm</span>
                                    </div>
                                    <div className="text-[11px] text-muted-foreground mt-1">{i.description}</div>
                                    {i.has_autofix && (
                                        <div className="text-[10px] text-emerald-400 mt-2">
                                            Auto-fix planned: {i.autofix_label} — will be applied at next 08:30 IST run or eCourts refresh.
                                        </div>
                                    )}
                                    {!i.has_autofix && (
                                        <div className="text-[10px] text-amber-400 mt-2">
                                            No safe auto-fix — please contact platform admin to investigate.
                                        </div>
                                    )}
                                    {(i.samples || []).length > 0 && (
                                        <details className="mt-2 text-[11px]">
                                            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">View {i.samples.length} sample{i.samples.length === 1 ? "" : "s"}</summary>
                                            <div className="font-mono mt-2 space-y-1">
                                                {i.samples.map((s, idx) => (
                                                    <div key={idx} className="text-muted-foreground">
                                                        <span className="text-foreground">{s.id}</span>
                                                        {s.snapshot && Object.keys(s.snapshot).length > 0 && (
                                                            <span> · {JSON.stringify(s.snapshot)}</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </details>
                                    )}
                                    {i.last_run_at && (
                                        <div className="text-[10px] text-muted-foreground mt-2 font-mono">
                                            Last checked: {new Date(i.last_run_at).toLocaleString()}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
